import { motion } from 'framer-motion';
import { CheckCircle2, Zap, Puzzle, Clock, Rocket } from 'lucide-react';

const trustPoints = [
  { icon: Zap,    title: 'Snabba projekt',      desc: 'Ingen lång implementationstid — ni ser resultat inom veckor.', color: 'from-indigo-500 to-violet-600' },
  { icon: Puzzle, title: 'Passar era system',    desc: 'Vi integrerar med det ni redan använder — inget behöver bytas.', color: 'from-violet-500 to-purple-600' },
  { icon: Clock,  title: 'Byggt för er skala',   desc: 'Lösningar anpassade för små och medelstora verksamheter.', color: 'from-purple-500 to-pink-500' },
  { icon: Rocket, title: 'Enkelt att testa',     desc: 'Börja i liten skala och skala upp när ni ser värdet.', color: 'from-amber-400 to-orange-500' },
];

const features = [
  'Du får se en prototyp innan du betalar en krona',
  'Vi integrerar med systemen du redan har',
  'Från idé till driftsatt på veckor, inte kvartal',
  'Fokus på lösningar som används direkt — inte PowerPoints',
];

const AboutSection = () => {
  return (
    <section className="py-28 bg-surface-subtle relative overflow-hidden">

      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-gradient-to-bl from-indigo-50 via-violet-50 to-transparent opacity-70 blur-3xl pointer-events-none" />

      <div className="container mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">

          {/* Left — text */}
          <div>
            <span className="inline-block text-xs font-semibold tracking-[0.18em] uppercase text-brand-indigo mb-5">
              Om Aifinity
            </span>
            <h2 className="text-4xl sm:text-[46px] font-extrabold text-text-primary tracking-[-0.02em] leading-[1.1] mb-6">
              Varför väljer<br />
              <span className="bg-gradient-to-r from-indigo-600 via-violet-600 to-purple-600 bg-clip-text text-transparent">
                företag Aifinity?
              </span>
            </h2>
            <p className="text-lg text-text-secondary leading-[1.75] mb-8">
              Vi är inget stort konsultbolag med långa offerter och ännu längre ledtider. Vi är ett litet, snabbt team som levererar fungerande system — inte presentationer.
            </p>

            <ul className="space-y-3.5 mb-10">
              {features.map((f) => (
                <li key={f} className="flex items-start gap-3 text-[15px] text-text-secondary">
                  <CheckCircle2 className="w-5 h-5 text-brand-indigo mt-0.5 shrink-0" />
                  {f}
                </li>
              ))}
            </ul>

            <a
              href="/contact"
              className="inline-flex items-center gap-2 text-sm font-semibold bg-gradient-to-r from-indigo-600 to-violet-600 bg-clip-text text-transparent hover:opacity-80 transition-opacity"
            >
              Prata med oss →
            </a>
          </div>

          {/* Right — trust point cards */}
          <div>
            <div className="grid grid-cols-2 gap-4">
              {trustPoints.map(({ icon: Icon, title, desc, color }) => (
                <motion.div
                  key={title}
                  whileHover={{ y: -4, transition: { duration: 0.2 } }}
                  className="group bg-white rounded-2xl p-6 border border-border-light shadow-sm hover:shadow-xl hover:shadow-indigo-100 transition-all duration-300"
                >
                  <div className={`inline-flex w-10 h-10 rounded-xl bg-gradient-to-br ${color} items-center justify-center mb-4 shadow-md`}>
                    <Icon className="w-4 h-4 text-white" />
                  </div>
                  <p className="text-[15px] font-bold text-text-primary mb-1">{title}</p>
                  <p className="text-xs text-text-secondary leading-snug">{desc}</p>
                </motion.div>
              ))}
            </div>

            {/* CTA box */}
            <div className="mt-5 p-5 rounded-2xl border border-indigo-100 bg-gradient-to-br from-indigo-50 to-violet-50">
              <p className="text-sm font-semibold text-text-primary mb-1">Byggt i Sverige</p>
              <p className="text-sm text-text-secondary leading-relaxed">
                Vi är ett litet, fokuserat team baserat i Sverige. Ni pratar direkt med den som bygger er lösning — ingen mellankommunikation.
              </p>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default AboutSection;
